import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const serviceDropdown = [
  { label: 'All Electronics Recycling', path: '/services', icon: 'ri-recycle-line', highlight: false },
  { label: 'TV Recycling', path: '/tv-recycling', icon: 'ri-tv-line', highlight: false },
  { label: 'Computer Recycling', path: '/computer-recycling', icon: 'ri-computer-line', highlight: false },
  { label: 'Hard Drive Destruction', path: '/hard-drive-destruction', icon: 'ri-hard-drive-line', highlight: false },
  { label: 'Battery Recycling', path: '/battery-recycling', icon: 'ri-battery-charge-line', highlight: true },
  { label: 'EV Charger Recycling', path: '/ev-charging-recycling', icon: 'ri-charging-pile-line', highlight: true },
];

const aboutDropdown = [
  { label: 'Locations', path: '/locations', icon: 'ri-map-pin-line' },
  { label: 'About Us', path: '/about', icon: 'ri-information-line' },
];

const topLinks = [
  { label: 'Home', path: '/' },
  { label: 'Battery Recycling', path: '/battery-recycling' },
  { label: 'E-Waste Events', path: '/events' },
  { label: 'Items We Recycle', path: '/faq' },
  { label: 'Contact', path: '/contact' },
];

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const location = useLocation();
  const servicesDropdownRef = useRef<HTMLDivElement>(null);
  const aboutDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
    setAboutOpen(false);
    setMobileServicesOpen(false);
    setMobileAboutOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
      if (aboutDropdownRef.current && !aboutDropdownRef.current.contains(e.target as Node)) {
        setAboutOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const servicesPaths = serviceDropdown.map((s) => s.path);
  const isServicesActive = servicesPaths.includes(location.pathname);

  const aboutPaths = aboutDropdown.map((a) => a.path);
  const isAboutActive = aboutPaths.includes(location.pathname);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#1B6B3A] border-b border-[#155530]">
      <nav className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src="/images/greenly.png"
            alt="Greenly Electronics Recycling Logo"
            className="h-10 md:h-11 w-auto object-contain"
            loading="eager"
          />
        </Link>

        {/* Desktop Links */}
        <ul className="hidden md:flex items-center gap-5">
          <li>
            <Link to="/" className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${location.pathname === '/' ? 'text-white font-bold underline underline-offset-4' : 'text-green-100 hover:text-white'}`}>
              Home
            </Link>
          </li>

          {/* Services Dropdown */}
          <li ref={servicesDropdownRef} className="relative">
            <button
              onClick={() => { setServicesOpen(!servicesOpen); setAboutOpen(false); }}
              className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${isServicesActive ? 'text-white font-bold underline underline-offset-4' : 'text-green-100 hover:text-white'}`}
            >
              Our Services
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {servicesOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl overflow-hidden z-50 border border-gray-100">
                {serviceDropdown.map((s) => (
                  <Link
                    key={s.path}
                    to={s.path}
                    className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors cursor-pointer ${
                      s.highlight
                        ? 'bg-amber-50 hover:bg-amber-100 border-t border-amber-100'
                        : location.pathname === s.path
                        ? 'bg-[#F4FAF7] text-[#1B6B3A] font-bold hover:bg-[#F4FAF7]'
                        : 'text-gray-700 hover:bg-[#F4FAF7]'
                    }`}
                  >
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 ${
                      s.highlight ? 'bg-amber-100' : 'bg-[#E8F5EE]'
                    }`}>
                      <i className={`${s.icon} text-xs ${
                        s.highlight ? 'text-amber-500' : 'text-[#1B6B3A]'
                      }`}></i>
                    </div>
                    <span className={s.highlight ? 'text-amber-700 font-semibold' : ''}>
                      {s.label}
                    </span>
                    {s.highlight && (
                      <span className="ml-auto text-[10px] font-bold text-amber-500 bg-amber-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        Free Pickup
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </li>

          {/* Other top links (Battery, Events, Items We Recycle) */}
          {topLinks.slice(1, 4).map((link) => {
            const isActive = location.pathname === link.path;
            const isBattery = link.path === '/battery-recycling';
            return (
              <li key={link.path}>
                {isBattery ? (
                  <Link
                    to={link.path}
                    className={`inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-amber-400 text-white'
                        : 'bg-amber-400/20 text-amber-300 hover:bg-amber-400/40 hover:text-amber-200'
                    }`}
                  >
                    <i className="ri-battery-charge-line text-xs"></i>
                    {link.label}
                  </Link>
                ) : (
                  <Link to={link.path} className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${isActive ? 'text-white font-bold underline underline-offset-4' : 'text-green-100 hover:text-white'}`}>
                    {link.label}
                  </Link>
                )}
              </li>
            );
          })}

          {/* About Us Dropdown */}
          <li ref={aboutDropdownRef} className="relative">
            <button
              onClick={() => { setAboutOpen(!aboutOpen); setServicesOpen(false); }}
              className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${isAboutActive ? 'text-white font-bold underline underline-offset-4' : 'text-green-100 hover:text-white'}`}
            >
              About Us
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${aboutOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {aboutOpen && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl overflow-hidden z-50 border border-gray-100">
                {aboutDropdown.map((a) => (
                  <Link
                    key={a.path}
                    to={a.path}
                    className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors cursor-pointer ${
                      location.pathname === a.path
                        ? 'bg-[#F4FAF7] text-[#1B6B3A] font-bold hover:bg-[#F4FAF7]'
                        : 'text-gray-700 hover:bg-[#F4FAF7]'
                    }`}
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#E8F5EE] shrink-0">
                      <i className={`${a.icon} text-xs text-[#1B6B3A]`}></i>
                    </div>
                    {a.label}
                  </Link>
                ))}
              </div>
            )}
          </li>

          {/* Contact */}
          {topLinks.slice(4).map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <li key={link.path}>
                <Link to={link.path} className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${isActive ? 'text-white font-bold underline underline-offset-4' : 'text-green-100 hover:text-white'}`}>
                  {link.label}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-2">
          <a href="tel:+12136001000" className="text-sm font-semibold whitespace-nowrap cursor-pointer text-green-100 hover:text-white transition-colors duration-200 mr-1">
            <i className="ri-phone-line mr-1"></i>(213) 600-1000
          </a>
          <Link to="/dropoff-form" className="bg-white text-[#1B6B3A] text-sm font-bold px-4 py-2 rounded-full hover:bg-green-50 transition-colors duration-200 whitespace-nowrap cursor-pointer">
            Drop Off e-Waste
          </Link>
          <Link to="/pickup-request" className="bg-[#4CAF82] text-white text-sm font-bold px-4 py-2 rounded-full hover:bg-[#3d9e70] transition-colors duration-200 whitespace-nowrap cursor-pointer">
            Request Pickup
          </Link>
        </div>

        {/* Hamburger */}
        <button className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg text-white hover:bg-white/10 transition-colors" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#155530] border-t border-[#0f4024]">
          <ul className="flex flex-col px-4 py-3 gap-1">
            <li>
              <Link to="/" className={`block px-4 py-[0.3rem] rounded-lg text-sm font-medium cursor-pointer transition-colors ${location.pathname === '/' ? 'bg-white/20 text-white font-bold' : 'text-green-100 hover:bg-white/10 hover:text-white'}`}>
                Home
              </Link>
            </li>
            {/* Mobile services accordion */}
            <li>
              <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className="w-full flex items-center justify-between px-4 py-[0.3rem] rounded-lg text-sm font-medium cursor-pointer text-green-100 hover:bg-white/10 hover:text-white transition-colors">
                <span>Our Services</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileServicesOpen && (
                <ul className="pl-4 pb-1 space-y-0.5">
                  {serviceDropdown.map((s) => (
                    <li key={s.path}>
                      <Link
                        to={s.path}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                          s.highlight
                            ? 'text-amber-300 hover:bg-amber-900/30 hover:text-amber-200'
                            : 'text-green-200 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <i className={`${s.icon} ${
                          s.highlight ? 'text-amber-400' : 'text-[#74C69D]'
                        }`}></i>
                        {s.label}
                        {s.highlight && (
                          <span className="ml-1 text-[10px] font-bold text-amber-400 bg-amber-900/40 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                            Free Pickup
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
            {/* Other top links (Battery, Events, Items We Recycle) */}
            {topLinks.slice(1, 4).map((link) => (
              <li key={link.path}>
                <Link to={link.path} className={`block px-4 py-[0.3rem] rounded-lg text-sm font-medium cursor-pointer transition-colors ${location.pathname === link.path ? 'bg-white/20 text-white font-bold' : 'text-green-100 hover:bg-white/10 hover:text-white'}`}>
                  {link.label}
                </Link>
              </li>
            ))}
            {/* Mobile About Us accordion */}
            <li>
              <button onClick={() => setMobileAboutOpen(!mobileAboutOpen)} className="w-full flex items-center justify-between px-4 py-[0.3rem] rounded-lg text-sm font-medium cursor-pointer text-green-100 hover:bg-white/10 hover:text-white transition-colors">
                <span>About Us</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileAboutOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileAboutOpen && (
                <ul className="pl-4 pb-1 space-y-0.5">
                  {aboutDropdown.map((a) => (
                    <li key={a.path}>
                      <Link
                        to={a.path}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium cursor-pointer transition-colors text-green-200 hover:bg-white/10 hover:text-white"
                      >
                        <i className={`${a.icon} text-[#74C69D]`}></i>
                        {a.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
            {/* Contact */}
            {topLinks.slice(4).map((link) => (
              <li key={link.path}>
                <Link to={link.path} className={`block px-4 py-[0.3rem] rounded-lg text-sm font-medium cursor-pointer transition-colors ${location.pathname === link.path ? 'bg-white/20 text-white font-bold' : 'text-green-100 hover:bg-white/10 hover:text-white'}`}>
                  {link.label}
                </Link>
              </li>
            ))}
            <li className="pt-2">
              <Link to="/dropoff-form" className="block text-center bg-white text-[#1B6B3A] text-sm font-bold px-4 py-3 rounded-full hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap">
                Drop Off e-Waste
              </Link>
            </li>
            <li className="pb-1">
              <Link to="/pickup-request" className="block text-center bg-[#4CAF82] text-white text-sm font-bold px-4 py-3 rounded-full hover:bg-[#3d9e70] transition-colors cursor-pointer whitespace-nowrap">
                Request Pickup
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Navbar;